import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { useSearchParams } from 'react-router-dom';

import { useIntl } from 'ha/i18n';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import {
  DATEPICKER_VARIANT,
  DateRangePicker,
  DateRangePickerProps,
  DateRange,
} from '@ha/marketplace/feature-date-picker';
import { syncDayWithUTC, normalizeDate } from '@ha/date';
import { constants } from 'ha/constants/commonConstants';
import { useUnitTypeId } from 'ha/modules/MultiUnitListings/hooks';
import { calculateMultiUnitExclusions } from 'ha/modules/MultiUnitListings/helpers/multiUnitExclusions';

import {
  getBookableWindows,
  getExclusions,
  getMinimalRentalPeriod,
  getMaximumStay,
  getIsMultiUnit,
  getBookablePeriods,
  getMaxBookableDays,
} from '../selectors';
import { setQueryParams } from '../actions';
import {
  ListingAdvancedPricingInfo,
  ListingAdvancedPricingPill,
} from '../fragments/ListingAdvancedPricing';
import { useDesiredDates } from '../hooks/useDesiredDates';

export function useDesiredDatesPicker() {
  const { T } = useIntl();
  const { md } = useMediaQuery();

  const [, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();

  const exclusions = useSelector(getExclusions);
  const isMultiUnit = useSelector(getIsMultiUnit);
  const bookablePeriods = useSelector(getBookablePeriods);

  const { isUnitTypeId: isTenantMULFlowEnabled } = useUnitTypeId();

  const minimumPeriod = useSelector(getMinimalRentalPeriod);
  const maximumStay = useSelector(getMaximumStay);

  const { desiredDates } = useDesiredDates();

  const prepareExclusions = React.useMemo((): DateRange[] => {
    const exclusionList = exclusions.map(exclusion => {
      return {
        from: syncDayWithUTC(new Date(exclusion.from)),
        to: syncDayWithUTC(new Date(exclusion.to)),
      };
    });

    if (isTenantMULFlowEnabled && isMultiUnit && !!desiredDates?.startDate) {
      return calculateMultiUnitExclusions(
        desiredDates,
        bookablePeriods,
        exclusionList,
      );
    }

    return exclusionList;
  }, [
    exclusions,
    isTenantMULFlowEnabled,
    isMultiUnit,
    desiredDates,
    bookablePeriods,
  ]);

  const currentValue = React.useMemo<DateRange>(
    () => ({
      from: desiredDates.startDate
        ? new Date(desiredDates.startDate)
        : undefined,
      to: desiredDates.endDate ? new Date(desiredDates.endDate) : undefined,
    }),
    [desiredDates.startDate, desiredDates.endDate],
  );

  const onDateChange = React.useCallback(
    (value: DateRange) => {
      let from;
      let to;

      if (value.from || value.to) {
        from = value.from;
        to = value.to;
      }

      const startDate = normalizeDate(from);
      const endDate = normalizeDate(to);

      // update query params in redux

      dispatch(
        setQueryParams({
          startDate,
          endDate,
        }),
      );

      // update URL query params

      if ((startDate && endDate) || (!startDate && !endDate)) {
        setSearchParams(
          searchParams => {
            if (startDate) {
              searchParams.set('startDate', startDate);
            } else {
              searchParams.delete('startDate');
            }

            if (endDate) {
              searchParams.set('endDate', endDate);
            } else {
              searchParams.delete('endDate');
            }
            return searchParams;
          },
          { replace: true },
        );
      }
    },
    [dispatch, setSearchParams],
  );
  const bookableWindows = useSelector(getBookableWindows);
  const maxBookableDays = useSelector(getMaxBookableDays);

  const moveInWindows = React.useMemo(
    () =>
      maxBookableDays
        ? bookableWindows.map(({ from, to }) => ({
            from: new Date(from),
            to: new Date(to),
          }))
        : [],
    [bookableWindows, maxBookableDays],
  );

  const dateRangePickerProps = React.useMemo<Partial<DateRangePickerProps>>(
    () => ({
      moveInWindows,
      exclusions: prepareExclusions,
      minimumPeriod,
      maximumStay,
      datePickerProps: {
        numberOfMonths: md ? 2 : 1,
        mode: 'range',
      },
      fromInputProps: {
        variant: 'filled',
      },
      toInputProps: {
        variant: 'filled',
      },
      fromInputLabel: T('Move-in date'),
      toInputLabel: T('Move-out date'),
      fromEmptyValueLabel: T('Select Date'),
      toEmptyValueLabel: T('Select Date'),
      clearDatesButton: T('Clear dates'),
      autoClose: true,
    }),
    [T, md, minimumPeriod, maximumStay, moveInWindows, prepareExclusions],
  );

  return { desiredDates, currentValue, onDateChange, dateRangePickerProps };
}

const useStyles = makeStyles()({
  inline: {
    display: 'inline-block',
  },
});

export const DesiredDatesPickerTrigger: React.FC<{
  variant?: 'block' | 'inline';
  children: React.FC<{ onClick: (event: React.MouseEvent) => void }>;
}> = ({ children: Component, variant = 'inline' }) => {
  const { classes } = useStyles();
  const { T, formatDate, locale } = useIntl();
  const { sm } = useMediaQuery();

  const { currentValue, onDateChange, dateRangePickerProps } =
    useDesiredDatesPicker();

  return (
    <div className={variant === 'inline' ? classes.inline : undefined}>
      <DateRangePicker
        header={<ListingAdvancedPricingInfo />}
        CaptionSubtitle={ListingAdvancedPricingPill}
        variant={DATEPICKER_VARIANT.RESPONSIVE}
        onDateChange={onDateChange}
        value={currentValue}
        {...dateRangePickerProps}
        DatePickerInputs={({ onFromFocus }) => (
          <Component
            onClick={event => {
              event.preventDefault();
              onFromFocus();
            }}
          />
        )}
        showSingleMonth={!sm}
        T={T}
        formatDate={formatDate}
        localeString={locale}
        listingFlexibleDays={constants.listingFlexibleDays}
      />
    </div>
  );
};
